import type {
	CustomerModelDTO,
	MarketViewAssetClassAlias,
	MarketViewSettings,
	SmartXSettings,
	SnapshotPolicy,
	SnapshotRule,
} from "@eu.mdotm.api/sphere";
import {
	BondDataAccessTier,
	ExportDtoExportConnectionTypeEnum,
	FundDataAccessTier,
	IntegrationEventEventEnum,
	InvestmentExportConverterType,
	MultiEntityConverterType,
	MarketScenarioIds,
	MarketViewAssetClassIdentifier,
	MarketViewSettingsActiveMarketViewTypesEnum,
	MarketViewType,
	SnapshotRuleTypeEnum,
} from "@eu.mdotm.api/sphere";
import { isExecutionWebhookActive } from "$root/functional-areas/portfolio/execution";
import { zExt } from "$root/third-party-integrations/zod";
import { MAX_PERCENT_DECIMAL_PLACES } from "$root/utils/monetary-math";
import type { WithTranslation } from "react-i18next";
import { z } from "zod";

export const CUSTOMER_DECIMAL_PLACES_MIN = 2;
export const CUSTOMER_DECIMAL_PLACES_MAX = MAX_PERCENT_DECIMAL_PLACES;

/** Seed values the "Create Customer" form opens with. Must itself satisfy {@link makeCustomerSchema}. */
export const defaultNewCustomerData: Partial<CustomerModelDTO> = {
	name: "",
	theme: "",
	maxUsers: 1000,
	maxPortfolios: 1000,
	decimalPreference: 2,
	enabled: false,
	marketViewSettings: { commentaryEditorVisible: true },
};

export const executionExclusivityMessage =
	"The Execute webhook and the export service are mutually exclusive — disable one to enable the other.";

/**
 * Whether a webhook API key is stored for the customer: `checking` until the existence endpoint answers, `unknown`
 * when it failed, `absent` for a customer not yet created.
 */
export const integrationApiKeyStatuses = ["checking", "stored", "absent", "unknown"] as const;
export type IntegrationApiKeyStatus = (typeof integrationApiKeyStatuses)[number];
type IntegrationFormOnlyKey = "apiKeyStatus" | "removeApiKey";
type McpBotFormOnlyKey = "removeApiKey";
type SmartXFormOnlyKey = "removeClientSecret" | "removePassword";

export const integrationApiKeyMessages = {
	required: "An enabled integration needs an API key: type one, or disable the integration.",
	unknown: "Could not check whether an API key is stored: type one to enable the integration.",
	removeWhileEnabled:
		"An enabled integration needs an API key: replace it instead of removing it, or disable the integration.",
} as const;

/** Why an ENABLED integration cannot be saved with the key state it has, or undefined when it can. */
export function enabledIntegrationApiKeyIssue(integration: {
	apiKey?: string | null;
	apiKeyStatus?: IntegrationApiKeyStatus | null;
	removeApiKey?: boolean | null;
}): string | undefined {
	if (integration.apiKey?.trim()) return undefined;
	if (integration.removeApiKey) return integrationApiKeyMessages.removeWhileEnabled;
	if (integration.apiKeyStatus === "stored") return undefined;
	return integration.apiKeyStatus === "unknown"
		? integrationApiKeyMessages.unknown
		: integrationApiKeyMessages.required;
}

/** The SmartX settings a customer switched on for SmartX cannot be saved without, in display order. */
export const smartXRequiredFields = ["enterpriseId", "clientId", "username", "clientSecret", "password"] as const;
export type SmartXRequiredField = (typeof smartXRequiredFields)[number];

/**
 * What the SmartX section still lacks while it is on. A secret counts when one is on file and not being removed,
 * or a new one is typed.
 */
export function missingSmartXSettings(
	settings:
		| ({ [K in keyof SmartXSettings]?: SmartXSettings[K] | null } & {
				[K in SmartXFormOnlyKey]?: boolean | null;
		  })
		| null
		| undefined,
): SmartXRequiredField[] {
	if (!settings?.enabled) return [];
	const filled = (value: string | null | undefined) => Boolean(value?.trim());
	const isSet = {
		enterpriseId: filled(settings.enterpriseId),
		clientId: filled(settings.clientId),
		username: filled(settings.username),
		clientSecret: (Boolean(settings.hasClientSecret) && !settings.removeClientSecret) || filled(settings.clientSecret),
		password: (Boolean(settings.hasPassword) && !settings.removePassword) || filled(settings.password),
	} satisfies Record<SmartXRequiredField, boolean>;
	return smartXRequiredFields.filter((field) => !isSet[field]);
}

export const SNAPSHOT_RETENTION_MONTHS_MIN = 1;
export const SNAPSHOT_RULE_DAYS_MIN = 1;
export const SNAPSHOT_RULE_DAYS_MAX = 31;

export type SnapshotPolicyFormValues = {
	enabled: boolean;
	retentionMonths: number | null;
	rules: SnapshotRule[];
};

/** The snapshot policy the dialog edits: a customer without one opens switched off with no rules. */
export function snapshotPolicyFormValues(stored: SnapshotPolicy | null | undefined): SnapshotPolicyFormValues {
	return {
		enabled: stored?.enabled ?? false,
		retentionMonths: stored?.retentionMonths ?? null,
		rules: stored?.rules ?? [],
	};
}

/** The snapshot policy as it goes on the wire. A customer that never had one keeps none until something is set. */
export function snapshotPolicyToSave(
	stored: SnapshotPolicy | null | undefined,
	group: SnapshotPolicyFormValues,
): SnapshotPolicy | undefined {
	const anythingSet = group.enabled || group.retentionMonths != null || group.rules.length > 0;
	if (!stored && !anythingSet) {
		return undefined;
	}
	return {
		enabled: group.enabled,
		retentionMonths: group.retentionMonths ?? undefined,
		rules: group.rules,
	};
}

/**
 * Validation schema behind the create/update-customer dialog.
 *
 * Extracted from the dialog so it can be exercised directly: the dialog disables its submit
 * button on `!formState.isValid`, so any field this schema rejects that has no visible input
 * locks the dialog with no on-screen explanation.
 *
 * Every field the DTO marks optional is `nullish()` rather than `optional()`. `optional()` admits
 * `undefined` but rejects `null`, so a backend that serialises an absent field as `null` instead
 * of omitting it would lock the dialog in exactly that invisible way.
 */
export function makeCustomerSchema(t: WithTranslation["t"]) {
	return z
		.object({
			// `nullish()`, not `optional()`: most customers have no import sync, and nothing in the
			// dialog renders an error for this object itself — a rejection here surfaces only as a
			// permanently disabled submit button.
			importData: z
				.object({
					enabled: z.boolean().nullish(),
					// MultiEntityConverterType, not InvestmentImportConverterType: this is the DTO's own
					// type, and it is what retrieveServicesType() fills the dropdown with.
					importServiceType: z.nativeEnum(MultiEntityConverterType).nullish(),
					password: z.string().nullish(),
					pollingFrequencyInMinutes: z.number().nullish(),
					port: z.number().nullish(),
					url: z.string().nullish(),
					username: z.string().nullish(),
				} satisfies Record<keyof NonNullable<CustomerModelDTO["importData"]>, unknown>)
				.nullish(),
			enabled: z.boolean().nullish(),
			name: zExt.nonEmptyString(t),
			theme: z.string().nullish(),
			maxUsers: z.number({ required_error: t("REQUIRED_FIELD") }),
			maxPortfolios: z.number().nullish(), // use line 832 when api will be ready
			decimalPreference: z
				.number()
				.int()
				.min(
					CUSTOMER_DECIMAL_PLACES_MIN,
					t("SETTING_PANEL.DECIMAL_PLACES_RANGE_ERROR", {
						min: CUSTOMER_DECIMAL_PLACES_MIN,
						max: CUSTOMER_DECIMAL_PLACES_MAX,
					}),
				)
				.max(
					CUSTOMER_DECIMAL_PLACES_MAX,
					t("SETTING_PANEL.DECIMAL_PLACES_RANGE_ERROR", {
						min: CUSTOMER_DECIMAL_PLACES_MIN,
						max: CUSTOMER_DECIMAL_PLACES_MAX,
					}),
				)
				.nullish(),
			bondDataAccessTier: z.nativeEnum(BondDataAccessTier).nullish(),
			fundDataAccessTier: z.nativeEnum(FundDataAccessTier).nullish(),
			exportData: z
				.object({
					exportConnectionType: z.nativeEnum(ExportDtoExportConnectionTypeEnum).nullish(),
					enabled: z.boolean().nullish(),
					directory: z.string().nullish(),
					password: z.string().nullish(),
					port: z.number().nullish(),
					exportServiceType: z.nativeEnum(InvestmentExportConverterType).nullish(),
					url: z.string().nullish(),
					username: z.string().nullish(),
				} satisfies Record<keyof NonNullable<CustomerModelDTO["exportData"]>, unknown>)
				.nullish(),
			id: z.string().nullish(),
			// decimalPreference: z.number().optional(),
			address: z.string().nullish(),
			backgroundImage: z.string().nullish(),
			clientLogo: z.string().nullish(),
			disclaimer: z.string().nullish(),
			email: z.string().nullish(),
			website: z.string().nullish(),
			marketViewSettings: z
				.object({
					activeMarketViewTypes: z.array(z.nativeEnum(MarketViewSettingsActiveMarketViewTypesEnum)).nullish(),
					customMarketViewName: z.string({ required_error: t("REQUIRED_FIELD") }).nullish(),
					customMarketViewType: z.nativeEnum(MarketViewType).nullish(),
					customMarketViewScenarioIds: z.array(z.nativeEnum(MarketScenarioIds)).nullish(),
					// customMarketViewMicroAssetClasses: z.any().optional(),
					commentaryEditorVisible: z.boolean().nullish(),
					customMarketViewAssetClasses: z.array(z.nativeEnum(MarketViewAssetClassIdentifier)).nullish(),
					defaultExpectedReturnMarketViewAssetClasses: z.array(z.nativeEnum(MarketViewAssetClassIdentifier)).nullish(),
					defaultPositioningMarketViewAssetClasses: z.array(z.nativeEnum(MarketViewAssetClassIdentifier)).nullish(),
					marketViewAssetClassAliases: z
						.array(
							z.object({
								alias: z.string().nullish(),
								microAssetClass: z.any().nullish(),
							} satisfies Record<keyof MarketViewAssetClassAlias, unknown>),
						)
						.nullish(),
				} satisfies Record<keyof MarketViewSettings, unknown>)
				.nullish(),

			// TODO: FIX BELOW
			profilePhoto: z.any().nullish(),
			maxRisk: z.any().nullish(),
			minRisk: z.any().nullish(),
			// TODO: FIX ABOVE

			// primaryPhone: z.string().optional(),
			primaryLogo: z.string().nullish(),
			submarkLogo: z.string().nullish(),
			// The webhook integration. `enabled` and `events` are the customer's; `apiKey` is write-only and holds
			// only what is typed into the dialog (a stored key is never sent to the browser). `apiKeyStatus` and
			// `removeApiKey` are form state, not DTO fields: whether a key is stored, as the existence endpoint
			// answered, and whether the admin chose to remove it. The dialog strips both before sending.
			integration: z
				.object({
					enabled: z.boolean().nullish(),
					hasApiKey: z.boolean().nullish(),
					apiKey: z.string().nullish(),
					events: z
						.array(
							z.object({
								event: z.nativeEnum(IntegrationEventEventEnum),
								url: z.string().url(),
							} satisfies Record<
								keyof NonNullable<NonNullable<CustomerModelDTO["integration"]>["events"]>[number],
								unknown
							>),
						)
						.nullish(),
					apiKeyStatus: z.enum(integrationApiKeyStatuses).nullish(),
					removeApiKey: z.boolean().nullish(),
				} satisfies Record<keyof NonNullable<CustomerModelDTO["integration"]> | IntegrationFormOnlyKey, unknown>)
				.nullish()
				.superRefine((data, ctx) => {
					if (!data?.enabled) return;
					if (!data.events?.length) {
						ctx.addIssue({
							code: "custom",
							path: ["events"],
							message: "add a valid hook",
						});
					}
					const apiKeyIssue = enabledIntegrationApiKeyIssue(data);
					if (apiKeyIssue) {
						ctx.addIssue({ code: "custom", path: ["apiKey"], message: apiKeyIssue });
					}
				}),

			// Sphere AI, per customer. `apiKey` is the customer's own Anthropic key —
			// sphere-mcp resolves it per chat turn so token spend is billed and capped
			// on THEIR Anthropic account, not ours. `hasApiKey` is the server's
			// read-only answer to "is one on file?", which is how the GET avoids
			// echoing the secret back. `webSearchEnabled` is the ceiling for the
			// assistant's web access: the per-chat switch can only opt out beneath it,
			// and the composer hides that switch entirely when this is off. `removeApiKey` is form state: the admin
			// chose to remove the key. The dialog strips it before sending.
			mcpBotSettings: z
				.object({
					apiKey: z.string().optional().nullable(),
					hasApiKey: z.boolean().optional().nullable(),
					webSearchEnabled: z.boolean().optional().nullable(),
					removeApiKey: z.boolean().nullish(),
				} satisfies Record<keyof NonNullable<CustomerModelDTO["mcpBotSettings"]> | McpBotFormOnlyKey, unknown>)
				.optional()
				.nullable(),

			// SmartX distribution. Like the Anthropic key, a stored secret is never sent to the browser:
			// `hasClientSecret` / `hasPassword` say whether one is on file. `removeClientSecret` / `removePassword`
			// are form state: the admin chose to remove that secret. The dialog strips them before sending.
			smartXSettings: z
				.object({
					enabled: z.boolean().nullish(),
					enterpriseId: z.string().nullish(),
					clientId: z.string().nullish(),
					username: z.string().nullish(),
					clientSecret: z.string().nullish(),
					password: z.string().nullish(),
					hasClientSecret: z.boolean().nullish(),
					hasPassword: z.boolean().nullish(),
					removeClientSecret: z.boolean().nullish(),
					removePassword: z.boolean().nullish(),
				} satisfies Record<keyof NonNullable<CustomerModelDTO["smartXSettings"]> | SmartXFormOnlyKey, unknown>)
				.nullish()
				.superRefine((data, ctx) => {
					for (const field of missingSmartXSettings(data)) {
						ctx.addIssue({ code: "custom", path: [field], message: t("REQUIRED_FIELD") });
					}
				}),

			snapshotPolicy: z
				.object({
					enabled: z.boolean().nullish(),
					retentionMonths: z
						.number()
						.int()
						.min(SNAPSHOT_RETENTION_MONTHS_MIN, `Retention must be at least ${SNAPSHOT_RETENTION_MONTHS_MIN} month`)
						.nullish(),
					rules: z
						.array(
							z.object({
								days: z
									.number({ required_error: t("REQUIRED_FIELD"), invalid_type_error: t("REQUIRED_FIELD") })
									.int()
									.min(
										SNAPSHOT_RULE_DAYS_MIN,
										`Days must be between ${SNAPSHOT_RULE_DAYS_MIN} and ${SNAPSHOT_RULE_DAYS_MAX}`,
									)
									.max(
										SNAPSHOT_RULE_DAYS_MAX,
										`Days must be between ${SNAPSHOT_RULE_DAYS_MIN} and ${SNAPSHOT_RULE_DAYS_MAX}`,
									),
								type: z.nativeEnum(SnapshotRuleTypeEnum, { required_error: t("REQUIRED_FIELD") }),
							} satisfies Record<keyof SnapshotRule, unknown>),
						)
						.nullish(),
				} satisfies Record<keyof SnapshotPolicy, unknown>)
				.nullish(),
		} satisfies Record<
			// `footerDescription`, `footerImage` and `integrationErrorMails` are carried by the DTO and held in the
			// database, but no frontend surface reads them, so the dialog offers no control for them.
			Exclude<
				keyof CustomerModelDTO,
				| "highRiskProfile"
				| "midRiskProfile"
				| "lowRiskProfile"
				| "footerDescription"
				| "footerImage"
				| "integrationErrorMails"
			>,
			unknown
		>)
		.superRefine((data, ctx) => {
			// Send-to-execution transports are mutually exclusive: EXECUTE webhook XOR export service.
			if (data.exportData?.enabled && isExecutionWebhookActive(data.integration)) {
				ctx.addIssue({ code: "custom", path: ["exportData", "enabled"], message: executionExclusivityMessage });
				ctx.addIssue({ code: "custom", path: ["integration", "events"], message: executionExclusivityMessage });
			}
		});
}
